import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, Sparkles } from '@react-three/drei';
import * as THREE from 'three';

export default function DeepCosmos() {
  const blackHoleDiskRef = useRef();
  const supernovaRef = useRef();
  const galaxyRef = useRef();

  // Procedural spiral galaxy texture
  const galaxyTexture = useMemo(() => {
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const cx = size / 2;
    const cy = size / 2;

    // Glowing core
    const radGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.48);
    radGrad.addColorStop(0, 'rgba(255, 250, 230, 1)');
    radGrad.addColorStop(0.15, 'rgba(168, 85, 247, 0.8)');
    radGrad.addColorStop(0.4, 'rgba(56, 189, 248, 0.4)');
    radGrad.addColorStop(0.7, 'rgba(30, 27, 75, 0.15)');
    radGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = radGrad;
    ctx.fillRect(0, 0, size, size);

    // Draw spiral arms
    ctx.strokeStyle = 'rgba(192, 132, 252, 0.35)';
    ctx.lineWidth = 12;
    for (let arm = 0; arm < 2; arm++) {
      ctx.beginPath();
      for (let theta = 0; theta < Math.PI * 4; theta += 0.1) {
        const r = (theta / (Math.PI * 4)) * (size * 0.42);
        const angle = theta + arm * Math.PI;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        if (theta === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    return new THREE.CanvasTexture(canvas);
  }, []);

  useFrame((_, delta) => {
    if (blackHoleDiskRef.current) {
      blackHoleDiskRef.current.rotation.z += delta * 0.08;
    }
    if (supernovaRef.current) {
      supernovaRef.current.rotation.y += delta * 0.02;
      supernovaRef.current.rotation.z += delta * 0.01;
    }
    if (galaxyRef.current) {
      galaxyRef.current.rotation.z += delta * 0.01;
    }
  });

  return (
    <group>
      {/* 1. DISTANT SUPERMASSIVE BLACK HOLE (Gargantua style) */}
      <group position={[-80, 28, -170]}>
        <Float speed={0.5} rotationIntensity={0.06} floatIntensity={0.15}>
          {/* Central Pitch-Black Event Horizon */}
          <mesh>
            <sphereGeometry args={[3.6, 32, 32]} />
            <meshBasicMaterial color="#000000" />
          </mesh>

          {/* Accretion Disk */}
          <group ref={blackHoleDiskRef} rotation={[Math.PI / 3, 0.4, 0]}>
            <mesh>
              <ringGeometry args={[4.2, 9.5, 64]} />
              <meshBasicMaterial
                color="#f97316"
                transparent
                opacity={0.8}
                side={THREE.DoubleSide}
                blending={THREE.AdditiveBlending}
                depthWrite={false}
              />
            </mesh>

            <mesh>
              <ringGeometry args={[3.8, 5.2, 64]} />
              <meshBasicMaterial
                color="#ffffff"
                transparent
                opacity={0.9}
                side={THREE.DoubleSide}
                blending={THREE.AdditiveBlending}
                depthWrite={false}
              />
            </mesh>
          </group>

          {/* Polar Jets */}
          <mesh position={[0, 8, 0]}>
            <cylinderGeometry args={[0.1, 0.8, 16, 8]} />
            <meshBasicMaterial
              color="#38bdf8"
              transparent
              opacity={0.5}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
          <mesh position={[0, -8, 0]}>
            <cylinderGeometry args={[0.8, 0.1, 16, 8]} />
            <meshBasicMaterial
              color="#38bdf8"
              transparent
              opacity={0.5}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>

          <pointLight intensity={1.5} color="#f97316" distance={50} />
        </Float>
      </group>

      {/* 2. EXPANDING SUPERNOVA REMNANT */}
      <group position={[85, 38, -135]} ref={supernovaRef}>
        <Float speed={0.6} rotationIntensity={0.08} floatIntensity={0.2}>
          <mesh>
            <sphereGeometry args={[1.2, 16, 16]} />
            <meshBasicMaterial color="#ffffff" />
          </mesh>
          <pointLight intensity={1.5} color="#ec4899" distance={50} />

          <mesh rotation={[0.4, 0.6, 0]}>
            <torusGeometry args={[8.5, 2.2, 16, 64]} />
            <meshBasicMaterial
              color="#ec4899"
              transparent
              opacity={0.3}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>

          <mesh rotation={[-0.5, 0.3, 0.8]}>
            <torusGeometry args={[11.0, 1.8, 16, 64]} />
            <meshBasicMaterial
              color="#38bdf8"
              transparent
              opacity={0.25}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>

          <Sparkles
            count={80}
            scale={[30, 30, 30]}
            size={2.5}
            speed={0.3}
            opacity={0.5}
            color="#f472b6"
          />
        </Float>
      </group>

      {/* 3. DISTANT SPIRAL GALAXY */}
      <group position={[50, -36, -155]}>
        <Float speed={0.4} rotationIntensity={0.04} floatIntensity={0.1}>
          <mesh ref={galaxyRef} rotation={[Math.PI / 3, 0.2, 0]}>
            <planeGeometry args={[36, 36]} />
            <meshBasicMaterial
              map={galaxyTexture}
              transparent
              opacity={0.55}
              side={THREE.DoubleSide}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        </Float>
      </group>

      {/* 4. GLOBULAR STAR CLUSTERS */}
      <group position={[-65, -30, -95]}>
        <Sparkles
          count={180}
          scale={[22, 22, 22]}
          size={2.2}
          speed={0.15}
          opacity={0.65}
          color="#60a5fa"
        />
      </group>

      <group position={[75, -20, 15]}>
        <Sparkles
          count={150}
          scale={[20, 20, 20]}
          size={2.4}
          speed={0.18}
          opacity={0.7}
          color="#fef08a"
        />
      </group>
    </group>
  );
}
